import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useAuth } from './useAuth';

export interface KitchenStation {
  id: string;
  location_id: string;
  name: string;
  description: string | null;
  display_categories: string[] | null;
  printer_config: Record<string, unknown> | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Optional: included when fetching with location details
  location?: {
    id: string;
    name: string;
  };
}

export type CreateKitchenStationInput = {
  location_id: string;
  name: string;
  description?: string | null;
  display_categories?: string[] | null;
  printer_config?: Record<string, unknown> | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
};

export type UpdateKitchenStationInput = Partial<Omit<KitchenStation, 'id' | 'location_id' | 'created_at' | 'updated_at'>>;

export interface UseKitchenStationsOptions {
  /** If true, fetch stations for all locations in the organization */
  allLocations?: boolean;
  /** Override location ID (when not using store) */
  locationId?: string | null;
}

export function useKitchenStations(options: UseKitchenStationsOptions = {}) {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();
  const { organization } = useAuth();
  const { allLocations = false, locationId: overrideLocationId } = options;

  // Use override location ID if provided, otherwise use store
  const locationId = overrideLocationId !== undefined ? overrideLocationId : currentLocation?.id;

  // Fetch kitchen stations - either for a specific location or all locations
  const {
    data: stations,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['kitchen-stations', allLocations ? 'all' : locationId, organization?.id],
    queryFn: async () => {
      if (allLocations) {
        // Fetch all stations for the organization (with location info)
        const { data, error } = await supabase
          .from('kitchen_stations')
          .select(`
            *,
            location:locations!inner(id, name)
          `)
          .order('sort_order')
          .order('name');

        if (error) throw error;
        return data as KitchenStation[];
      }

      if (!locationId) return [];

      const { data, error } = await supabase
        .from('kitchen_stations')
        .select('*')
        .eq('location_id', locationId)
        .order('sort_order')
        .order('name');

      if (error) throw error;
      return data as KitchenStation[];
    },
    enabled: allLocations ? !!organization?.id : !!locationId,
  });

  // Get active stations only (for use in product forms, routing, etc.)
  const activeStations = stations?.filter(s => s.is_active) ?? [];

  // Create a new kitchen station
  const createStation = useMutation({
    mutationFn: async (station: CreateKitchenStationInput) => {
      const { data, error } = await supabase
        .from('kitchen_stations')
        .insert({
          ...station,
          color: station.color || '#6366f1',
          sort_order: station.sort_order ?? 0,
          is_active: station.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data as KitchenStation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-stations', locationId] });
    },
  });

  // Update an existing kitchen station
  const updateStation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateKitchenStationInput }) => {
      const { data, error } = await supabase
        .from('kitchen_stations')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as KitchenStation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-stations', locationId] });
      // Also invalidate kitchen orders since station names may have changed
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId] });
    },
  });

  // Delete a kitchen station (hard delete - stations are not soft-deleted)
  const deleteStation = useMutation({
    mutationFn: async (id: string) => {
      // First, unassign any products that were assigned to this station
      await supabase
        .from('products')
        .update({ station_id: null })
        .eq('station_id', id);

      // Unassign any kitchen orders that were assigned to this station to prevent FK conflict
      await supabase
        .from('kitchen_orders')
        .update({ station_id: null })
        .eq('station_id', id);

      const { error } = await supabase
        .from('kitchen_stations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-stations', locationId] });
      queryClient.invalidateQueries({ queryKey: ['products', locationId] });
    },
  });

  // Toggle active status
  const toggleStationActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('kitchen_stations')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as KitchenStation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-stations', locationId] });
    },
  });

  // Update display categories for a station
  const updateDisplayCategories = useMutation({
    mutationFn: async ({ id, categoryIds }: { id: string; categoryIds: string[] }) => {
      const { data, error } = await supabase
        .from('kitchen_stations')
        .update({
          display_categories: categoryIds,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as KitchenStation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-stations', locationId] });
    },
  });

  // Reorder stations
  const reorderStations = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const updates = orderedIds.map((id, index) => ({
        id,
        sort_order: index,
        updated_at: new Date().toISOString(),
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('kitchen_stations')
          .update({ sort_order: update.sort_order, updated_at: update.updated_at })
          .eq('id', update.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-stations', locationId] });
    },
  });

  return {
    stations: stations ?? [],
    activeStations,
    isLoading,
    error,
    refetch,
    createStation,
    updateStation,
    deleteStation,
    toggleStationActive,
    updateDisplayCategories,
    reorderStations,
    locationId,
  };
}
